import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, App, Alert } from 'antd';
import cioService from '../../Services/cioService';
import { DeploymentStatusLabels, optionsFromLabels } from '../../Constants/enums';
import { toApiError } from '../../api/axiosClient';

/**
 * Register the prepared dedicated customer environment (handoff Flow F +
 * Configure Dedicated Deployment). Secret references are operational fields;
 * per the handoff the frontend never receives or stores DB passwords or full
 * connection strings, and status should only be Ready when actually reachable.
 */
const DeploymentModal = ({ open, onClose, clientId, initial, onSaved }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      frontendUrl: initial?.frontendUrl || '',
      apiBaseUrl: initial?.apiBaseUrl || '',
      appDatabaseName: initial?.appDatabaseName || '',
      hiringDatabaseName: initial?.hiringDatabaseName || '',
      instanceKey: initial?.instanceKey || '',
      status: initial?.status ?? 0,
      appDbSecretReference: '',
      hiringDbSecretReference: '',
    });
  }, [open, initial, form]);

  const onOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setSaving(true);
    try {
      await cioService.setDeployment(clientId, values);
      message.success('Deployment saved.');
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Configure Dedicated Deployment"
      open={open}
      onOk={onOk}
      onCancel={onClose}
      confirmLoading={saving}
      okText="Save deployment"
      width={640}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Only set status to Ready once the frontend, API, and databases are actually reachable. Never enter database passwords or full connection strings here."
      />
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item name="frontendUrl" label="Frontend URL" rules={[{ required: true }]}>
          <Input placeholder="https://customer.velorhr.com" />
        </Form.Item>
        <Form.Item name="apiBaseUrl" label="API base URL" rules={[{ required: true }]}>
          <Input placeholder="https://api.customer.velorhr.com" />
        </Form.Item>
        <Form.Item name="appDatabaseName" label="Application database name" rules={[{ required: true }]}>
          <Input placeholder="VelorHR_Customer_App" />
        </Form.Item>
        <Form.Item name="hiringDatabaseName" label="Hiring database name" rules={[{ required: true }]}>
          <Input placeholder="VelorHR_Customer_Hiring" />
        </Form.Item>
        <Form.Item name="instanceKey" label="Instance key" rules={[{ required: true }]}>
          <Input placeholder="customer" />
        </Form.Item>
        <Form.Item name="status" label="Deployment status" rules={[{ required: true }]}>
          <Select options={optionsFromLabels(DeploymentStatusLabels)} />
        </Form.Item>
        <Form.Item name="appDbSecretReference" label="App DB secret reference (optional)">
          <Input placeholder="Reference name only - not a password" />
        </Form.Item>
        <Form.Item name="hiringDbSecretReference" label="Hiring DB secret reference (optional)">
          <Input placeholder="Reference name only - not a password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DeploymentModal;
